import React, { useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ValidationContext } from './validationContext'
import Icon from '@/components/Icon'
import { format } from '@/utils/StringUtilities'
import LoadingIndicator from '@/components/LoadingIndicator'
import { isDefined } from '@/utils/isDefined'

const Validator = React.memo(function Validator ({ name, value, disabled, children, required, regex, maxLength, min, max, onValidate }) {
  const context = useContext(ValidationContext)
  const [pristine, setPristine] = useState(true)
  const [origValue] = useState(value)
  const [errorMessage, setErrorMessage] = useState(undefined)

  useEffect(() => {
    function validate () {
      if (disabled) return
      if (required && typeof value !== 'number' && !value) {
        return 'Validations.Required'
      }
      if (regex && typeof value === 'string') {
        if (!new RegExp(regex).test(value)) {
          return 'Validations.InvalidFormat'
        }
      }
      if (typeof value === 'string') {
        if (maxLength && value.length > maxLength) {
          return 'Validations.MaxLengthExceeded'
        }
        if (required && value.trim() === '') {
          return 'Validations.Required'
        }
      }
      if (typeof value === 'number') {
        if (isDefined(min) && value < min) {
          return ['Validations.LessThanMinValue', min]
        }
        if (isDefined(max) && value > max) {
          return ['Validations.MaxValueExceeded', max]
        }
      }
      if (typeof onValidate === 'function') {
        return onValidate({ name, value, required, regex })
      }
    }

    function shouldUpdateContext (errorMessage) {
      const { errors } = context
      const error = errors.find(e => e.field === name)
      if (!error) {
        if (errorMessage) return true
      } else {
        if (errorMessage) {
          const errMessage = Array.isArray(errorMessage) ? errorMessage[0] : errorMessage
          if (errMessage !== error.error) return true
        } else {
          return true
        }
      }
      return false
    }

    const msg = validate() || getServerValidationError(context, name)
    setErrorMessage(msg)

    if (disabled) {
      if (shouldUpdateContext()) {
        context.onValidate({ field: name, error: null })
      }
    }
    if (pristine && origValue !== value) {
      setPristine(false)
    }
    if (shouldUpdateContext(msg)) {
      const errMessage = Array.isArray(msg) ? msg[0] : msg
      context.onValidate({ field: name, error: errMessage })
    }
  }, [context, disabled, max, maxLength, min, name, onValidate, origValue, pristine, regex, required, value])

  if (disabled) {
    return children
  }
  // if (pristine && origValue !== value) {
  //   return null // react should re-render this component immediately, so we can skip the rest in this execution
  // }

  const css = errorMessage ? 'input-invalid has-error' : 'input-valid'
  return (
    <div className={css}>
      {children}
      <Icon name={errorMessage ? (pristine ? 'error-circle' : 'error-circle red') : 'check green'} />
      {renderErrorMessage(errorMessage)}
    </div>
  )

  function renderErrorMessage (errorMessage) {
    if (errorMessage && context.localize) {
      // if fetching then return loading indicator
      if (errorMessage === 'Validations.Fetching') {
        return <div className='validation-fetching'><LoadingIndicator inline /></div>
      }

      // omit error message for required fields - indicated with error circle icon already
      if (errorMessage !== 'Validations.Required') {
        const errMessage = Array.isArray(errorMessage) ? errorMessage[0] : errorMessage
        const params = Array.isArray(errorMessage) ? errorMessage.slice(1) : [name]
        return <div className='help-text'>{format(context.localize(errMessage), ...params)}</div>
      }
    }
    return null
  }
})

Validator.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  regex: PropTypes.string,
  onValidate: PropTypes.func,
  maxLength: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  children: PropTypes.node
}

function getServerValidationError (context, field) {
  if (context.serverError && context.serverError.validationErrors && context.serverError.validationErrors.length) {
    const err = context.serverError.validationErrors.find(x => x.members.find(m => m === field))
    return err && err.message && err.message.replace(':', '.').split('##')
  }
  return null
}

export {
  Validator
}



// WEBPACK FOOTER //
// ./src/components/validation/validator.js